import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { useCallback, useEffect, useState } from 'react';
import { removeCpfFormatting } from 'utils/formatters';
import { useStoreSelector } from 'v3/data/store';

import { showErrorDialog } from 'v3/data/store/error/actions';
import {
  setAllSellerList,
  setSellerInvalidRowsModalOpen,
  setSellerStoreIdsListFilter,
  setSellerStoreList,
} from 'v3/data/store/seller/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';

import { sanitizeCpfOrName } from 'utils/sanitizeCpfOrName';
import { IStoreItem } from 'v3/data/store/stores/types';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { ICreateSeller } from 'v3/pages/Seller/List/types';
import { Container, Content, Header } from 'v3/pages/Stores/List/styles';
import * as api from 'v3/services/api';
import { listAllSellers } from 'v3/services/api';
import { CircularButton } from 'v3/shared/components/Button/CircularButton';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { MiniButton } from 'v3/shared/components/Button/MiniButton';
import { SellersFilters } from 'v3/shared/components/Filters/SellersFilter';
import { DownloadModal } from 'v3/shared/components/Modal/DownloadModal';
import { SellerFormModal } from 'v3/shared/components/Modal/SellerFormModal';
import { SellerInvalidRowsModal } from 'v3/shared/components/Modal/SellerInvalidRowsmodal';
import { UploadModal } from 'v3/shared/components/Modal/UploadModal';
import { SellerTable } from 'v3/shared/components/Table/SellerTables';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { CommonTemplate } from 'v3/shared/templates/CommonTemplate';
import { HeadlineMedium } from 'v3/shared/typography/HeadlineMedium';
import { theme } from 'v3/styles/global-styles';
import { AlignButtons, AlignMobileButtons, IconColorDownload, IconColorUpload } from './styles';

export const SellerList = () => {
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const { isMobile } = useBreakpoints();
  const { group, profileId, id, storeUserId } = useStoreSelector((store) => store.store.storeLogged);
  const { list } = useStoreSelector((store) => store.contracting);
  const { sellersFilter } = useStoreSelector((store) => store.seller);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [sellerBankAccountFeatureEnabled, setSellerBankAccountFeatureEnabled] = useState(false);

  const { data, sellerInvalidRowsModalOpen } = useStoreSelector((store) => store.seller.uploadSellerFileResponse);
  const { page, rowsPerPage } = sellersFilter;

  const extractStoreIds = (data: IStoreItem[]) => {
    return data.map((store) => store?.id);
  };

  const fetchStoreIdsList = useCallback(async () => {
    try {
      const response = await api.getStores({ group, profileId, storeUserId });
      const storeIds = extractStoreIds(response.data);
      setSellerStoreIdsListFilter(storeIds);
      const storeListFomated = response.data.map((item) => {
        return {
          ...item,
          checked: true,
        };
      });
      setSellerStoreList(storeListFomated);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de lojas', error);
    }
  }, [id, profileId]);

  const sellerAlreadyCreated = async (cpf: string, storeId: string): Promise<boolean> => {
    try {
      const formattedCpf = removeCpfFormatting(cpf);
      await api.findSeller(formattedCpf, storeId);
      return true;
    } catch (error) {
      return false;
    }
  };

  const fetchSellerList = async () => {
    try {
      const response = await listAllSellers({
        rowsPerPage,
        page,
        searchString: sanitizeCpfOrName(sellersFilter.filter),
        active: sellersFilter.active,
        storeIds: sellersFilter.storeIds,
      });
      setAllSellerList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista dos consultores', error);
    }
  };

  const createNewSeller = async (data: ICreateSeller, reset) => {
    const sellerDuplicatedByStore = await sellerAlreadyCreated(data.code, data.storeId);
    if (sellerDuplicatedByStore) {
      showSnackbar('Este CPF já está cadastrado para essa loja.', 'error', true);
      return;
    }

    const newSellerPayload = {
      code: removeCpfFormatting(data.code),
      storeId: data.storeId,
      name: data.name,
      role: sellerBankAccountFeatureEnabled ? data.role : undefined,
      profileId: data.profileId,
      email: sellerBankAccountFeatureEnabled ? data.email : undefined,
      pixKey: sellerBankAccountFeatureEnabled ? data.pixKey : undefined,
      phoneAreaCode: sellerBankAccountFeatureEnabled ? data.phoneAreaCode : undefined,
      phoneNumber: sellerBankAccountFeatureEnabled ? data.phoneNumber : undefined,
    };

    try {
      await api.createSeller(newSellerPayload);

      reset();
      fetchSellerList();
      setChangeModalOpen(false);
      showSnackbar('Consultor(a) cadastrado com sucesso', 'success', true);
    } catch (error) {
      showSnackbar(
        'Algo deu errado. O CPF pode já estar cadastrado em outro grupo ou houve um erro ao processar a solicitação',
        'error',
        true,
      );
      console.error(error);
    }
  };

  const handleButtonClick = () => {
    setChangeModalOpen(true);
  };

  useEffect(() => {
    storeUserId && fetchStoreIdsList();
  }, [profileId, fetchStoreIdsList]);

  const fetchSellerFeatureFlag = useCallback(async () => {
    const featureBackofficeResponse = await api.getFlagStatus(
      FeatureFlagEnum.IKCLOUD_12852_SELLER_BANK_ACCOUNT_BACKOFFICE,
    );

    setSellerBankAccountFeatureEnabled(featureBackofficeResponse.data.enabled);
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, []);

  return (
    <CommonTemplate>
      <Container>
        <Content>
          <Header>
            <HeadlineMedium>Lista de Consultores</HeadlineMedium>

            {isMobile ? (
              <AlignMobileButtons>
                <CircularButton
                  startIcon={IconColorDownload}
                  backgroundColor={theme.palette.secondary.light}
                  handleClick={() => setOpenDownloadModal(true)}
                />
                <CircularButton
                  startIcon={IconColorUpload}
                  backgroundColor={theme.palette.secondary.light}
                  handleClick={() => setOpenUploadModal(true)}
                />
                <CircularButton
                  startIcon={PersonAddAltIcon}
                  backgroundColor={theme.palette.tertiary.main}
                  handleClick={handleButtonClick}
                />
              </AlignMobileButtons>
            ) : (
              <AlignButtons>
                <MiniButton
                  $backgroundColor="transparent"
                  icon={FileDownloadOutlinedIcon}
                  $outline
                  handleClick={() => setOpenDownloadModal(true)}
                />
                <MiniButton
                  $backgroundColor="transparent"
                  icon={FileUploadOutlinedIcon}
                  $outline
                  handleClick={() => setOpenUploadModal(true)}
                />
                <FlexButton
                  size="small"
                  startIcon={PersonAddAltIcon}
                  text={'Adicionar consultor'}
                  handleClick={handleButtonClick}
                />
              </AlignButtons>
            )}
          </Header>
          <SellersFilters />
          <SellerTable />
        </Content>
        <SellerFormModal
          modal={{
            active: changeModalOpen,
            title: 'Adicionar consultor(a)',
            activeButton: true,
            cancelButton: 'Cancelar',
            saveButton: 'Salvar',
            type: 'create',
          }}
          onClose={() => setChangeModalOpen(false)}
          onConfirm={createNewSeller}
        />
      </Container>
      <DownloadModal open={openDownloadModal} setOpenModal={setOpenDownloadModal} list={list} />
      <UploadModal open={openUploadModal} setOpenModal={setOpenUploadModal} list={list} />
      <SellerInvalidRowsModal
        active={sellerInvalidRowsModalOpen}
        data={data}
        onClose={() => {
          setSellerInvalidRowsModalOpen(false);
        }}
        onConfirm={() => {
          setSellerInvalidRowsModalOpen(false);
        }}
      />
    </CommonTemplate>
  );
};
